/**
 * ImageUploadWidget Component - Story 6.3: Create Add Product Form
 * Main image upload widget with drag-drop support for product images
 */

import { useState, useRef, useCallback } from 'react'
import { Upload, Image as ImageIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Image as ImageType } from '../../types/productForm.types'
import { FORM_CONFIG } from '../../constants/productFormConstants'
import { ImagePreview } from './ImagePreview'
import './ImageUploadWidget.css'

export interface ImageUploadWidgetProps {
  images: ImageType[]
  onAddImages: (files: File[]) => void
  onRemoveImage: (index: number) => void
  isLoading?: boolean
  maxImages?: number
  disabled?: boolean
}

/**
 * ImageUploadWidget Component
 * Provides drag-and-drop and file browser functionality for uploading product images
 */
export function ImageUploadWidget({
  images,
  onAddImages,
  onRemoveImage,
  isLoading = false,
  maxImages = FORM_CONFIG.MAX_IMAGES,
  disabled = false
}: ImageUploadWidgetProps) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith('image/')
      )

      if (droppedFiles.length > 0) {
        onAddImages(droppedFiles)
      }
    },
    [onAddImages]
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.currentTarget.files || [])
      if (selectedFiles.length > 0) {
        onAddImages(selectedFiles)
      }
      // Reset input so same file can be selected again
      e.currentTarget.value = ''
    },
    [onAddImages]
  )

  // Click handler for the entire upload area - opens file picker
  const handleUploadAreaClick = useCallback((e: React.MouseEvent) => {
    if (isLoading || disabled) return
    // Don't trigger if clicking on the file input itself (it handles its own click)
    const target = e.target as HTMLElement
    if (target.tagName === 'INPUT') return
    fileInputRef.current?.click()
  }, [isLoading, disabled])

  const canAddMore = images.length < maxImages
  const remainingSlots = maxImages - images.length

  return (
    <div className="image-upload-widget">
      {/* Upload Area - entire area is clickable to open file picker */}
      {canAddMore && (
        <div
          className={cn(
            'upload-area',
            isDragging && 'dragging',
            (isLoading || disabled) && 'disabled'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadAreaClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (!isLoading && !disabled) {
                fileInputRef.current?.click()
              }
            }
          }}
          aria-label="Click to upload product images"
        >
          <div className="upload-content">
            <Upload className="upload-icon" size={48} />
            <h3 className="upload-title">Drag and drop images here</h3>
            <p className="upload-description">or click anywhere to browse files</p>

            <div
              className={cn(
                'inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm',
                !(isLoading || disabled) && 'hover:bg-accent hover:text-accent-foreground',
                (isLoading || disabled) && 'opacity-50'
              )}
            >
              Browse Files
            </div>

            {/* File input - visually hidden but still in the DOM and functional */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileInputChange}
              disabled={isLoading || disabled}
              aria-label="Upload product images"
              tabIndex={-1}
              style={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
                opacity: 0.01,
                pointerEvents: 'none',
              }}
            />

            <p className="upload-hint">
              Supported formats: JPEG, PNG, WebP (Max {FORM_CONFIG.MAX_FILE_SIZE_MB}MB each)
            </p>
            <p className="upload-limit">
              {remainingSlots} of {maxImages} slots remaining
            </p>
          </div>
        </div>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="image-grid">
          {images.map((image, index) => (
            <ImagePreview
              key={index}
              image={image}
              index={index}
              onRemove={onRemoveImage}
              disabled={isLoading || disabled}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {images.length === 0 && !canAddMore && (
        <div className="max-images-reached">
          <ImageIcon size={32} />
          <p>Maximum images limit reached ({maxImages} images)</p>
        </div>
      )}

      {/* Status Messages */}
      {isLoading && <p className="text-center text-sm text-gray-500">Uploading images...</p>}
    </div>
  )
}
